import { useEffect, useId, useState } from "react";
import { CalendarDays } from "lucide-react";
import type { ReportingDateFilter } from "../../communityReportingApi";
import type { AppliedPeriod } from "./useReportingPeriod";
import { useObservatoryCopy } from "./copy";

export function ReportingDateRange({
  bounds,
  applied,
  loading,
  resetKey,
  onApply,
  onClear,
}: {
  bounds?: ReportingDateFilter | null | undefined;
  applied: AppliedPeriod | null;
  loading: boolean;
  resetKey: number;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}) {
  const { t } = useObservatoryCopy();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [invalid, setInvalid] = useState(false);
  const id = useId();
  useEffect(() => {
    setFrom(applied?.from ?? "");
    setTo(applied?.to ?? "");
    setInvalid(false);
  }, [resetKey, applied?.from, applied?.to]);
  const available = !!bounds?.available;
  const unavailableId = `${id}-unavailable`;
  const unavailableMessage =
    bounds?.min_date && bounds.max_date
      ? t("datesNeedPublication")
      : t("datesUnavailable");
  function openCalendar(input: HTMLInputElement) {
    if (!available || loading) return;
    try {
      input.showPicker?.();
    } catch {
      // Some browsers/embedded contexts reject programmatic pickers. Native
      // calendar icons and keyboard date entry must remain usable.
    }
  }
  return (
    <form
      className="observatory-date-range"
      aria-label={t("dateRange")}
      onSubmit={(event) => {
        event.preventDefault();
        if (!available) return;
        if (!from && !to) return onClear();
        const first = from || bounds!.min_date!;
        const last = to || bounds!.max_date!;
        if (
          first > last ||
          first < bounds!.min_date! ||
          last > bounds!.max_date!
        ) {
          setInvalid(true);
          return;
        }
        setInvalid(false);
        onApply(first, last);
      }}
    >
      <span className="observatory-date-caption">
        <CalendarDays size={16} aria-hidden="true" />
        {t("dateRange")}
      </span>
      <label>
        {t("dateFrom")}
        <input
          type="date"
          dir="ltr"
          onClick={(event) => openCalendar(event.currentTarget)}
          disabled={!available || loading}
          value={from}
          min={bounds?.min_date ?? undefined}
          max={bounds?.max_date ?? undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={
            invalid ? id : !available ? unavailableId : undefined
          }
          onChange={(event) => {
            setFrom(event.target.value);
            setInvalid(false);
          }}
        />
      </label>
      <label>
        {t("dateTo")}
        <input
          type="date"
          dir="ltr"
          onClick={(event) => openCalendar(event.currentTarget)}
          disabled={!available || loading}
          value={to}
          min={bounds?.min_date ?? undefined}
          max={bounds?.max_date ?? undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={
            invalid ? id : !available ? unavailableId : undefined
          }
          onChange={(event) => {
            setTo(event.target.value);
            setInvalid(false);
          }}
        />
      </label>
      <button type="submit" disabled={!available || loading}>
        {t("applyDates")}
      </button>
      <button
        type="button"
        disabled={!applied && !from && !to}
        onClick={() => {
          setFrom("");
          setTo("");
          setInvalid(false);
          onClear();
        }}
      >
        {t("allDates")}
      </button>
      <span className="observatory-period-applied">
        {applied ? (
          <bdi dir="ltr">
            {applied.from} — {applied.to}
          </bdi>
        ) : null}
      </span>
      {!available && (
        <span id={unavailableId} className="observatory-date-help">
          {unavailableMessage}
        </span>
      )}
      {invalid && (
        <span id={id} role="alert" data-kavoosh-inline-validation="true">
          {t("invalidDates")}
        </span>
      )}
    </form>
  );
}
